import React, { Component } from 'react'
import { connect } from 'react-redux'
import _, { isNumber, keys } from 'lodash'
import fp from 'lodash/fp'
import moment from 'moment'
import FileSaver from 'file-saver'
import { Modal, Glyphicon, Checkbox } from 'react-bootstrap'

import { findNthIndex } from '../../lib/Tools.js'
import { getTransportationLabel, getText } from '../lib/text'
import { createExcelBlob, planDeViajeStyle } from '../../lib/xlsx.js'
import Button from '../components/Button.jsx'
import { getBillForProps } from '../selectors'

/**
 * WARNING
 * generateCSVData está muy acoplado con getTripBill
 * depende de que la primera sección sean los gastos, la segunda el transporte, y luego las actividades
 * esto puede cambiar (y cambiará, por ejemplo, cuando lleguen los hoteles
 * 
 * solución: identificar cada section con un type o algo y usar eso
 * 
 * TODO: desacoplar generateCSVData de getTripBill usando etiquetas en lugar de el orden para identificar secciones
 */

const getDay = (agenda, nth) => {
  const d0 = findNthIndex(agenda, nth, x => x.type == "day")

  if (isNumber(d0)) { 
    const d1 = _.findIndex(agenda, x => x.type == "day", d0+1)
    if (isNumber(d1)) 
      return agenda.slice(d0, d1)
  }
  
  return null
}

const getCitiesColumn = getCity => fp.flow(
  fp.map(x => (x.city ? getCity(x.city).name : null)), 
  _.compact,
  _.uniq
)

const getActivityNamesColumn = getActivity => fp.flow(
  fp.filter(x => x.type == "activity"), 
  fp.map(x => getActivity(x.id).englishName || getActivity(x.id).name)
)

const getActivityTitlesColumn = getActivity => fp.flow(
  fp.filter(x => x.type == "activity"),
  fp.map(x => getActivity(x.id).title || "")
)

const getTransportColumn = getCity => fp.flow(
  fp.filter(x => x.type == "city"), 
  fp.map(x => `${getTransportationLabel(x.transport.means, {forceLanguage: "en"})} to ${getCity(x.city).name}`)
)

const isHotel = (item, expenses) => (
  item.type == "expense" && (
    (item.expense && item.expense.type == "hotel") ||
    (item.ref && expenses[item.ref].type == "hotel")
  )
)

const getHotelColumn = (items: Object[], expenses: Object) => {
  const item = _.find(items, x => isHotel(x, expenses))
  const expense = item && (item.expense || expenses[item.ref])
  return [
    `Hotel: ${expense ? expense.name : ""}`,
    `Address: `, 
    `Phone number: `
  ]
}

const getNumDays = agenda => agenda.filter(x => x.type == "day").length

/*Detectamos como sustituir un viaje a en un retorno y devolvemos un array de string con el nuevo resultado 
Esta funcion se ejecuta todos los dias del trip. Se recibe el array de strings y la ciudad en la que empieza el dia.
Tenemos una pila de los viajes del dia, inicialmente con la ciudad en la que empieza.
Cada vez que viaja a una ciudad, se comprueba si esa ciudad estaba en la pila; si lo estaba, entonces es que vuelve a ella,
y hacemos un cambio en el string con regex, sino se añade esa ciudad a la pila.
*/
function transportColumnTreatment(transportations, firstCity) {
  const cityFlow = [firstCity]
  let city = firstCity
  let n = []
  transportations.forEach(function (x, i) {
    n = x.split(" ")
    city = n[n.length - 1]
    if (cityFlow.includes(city)) {
      transportations[i] = x.replace(/.*(?=to)/i, 'Return ') + ` (${x.match(/.*(?=to)/i)})`
    } else {
      cityFlow.push(city)
    }
  })
  return {transportations, city}
}

function generateCSV(trip, route, bill, getCity, getActivity, includeLastHotel) {
  const agenda = route.agenda
  const emptyRow = ["", "", "", "", "", ""]
  const sd        = moment(trip.startDate)
  let rows = []
  let currentCity = ""
  let transportations

  /* collect the expenses (to resolve references) */
  const expenses = {}
  agenda.forEach(item => { if (item.expense) expenses[item.key] = item.expense })
  rows.push(["", "", "", "Trip plan", "", ""])  
  rows.push(["", "City", "Visit Plan", "", "Transportation", "Hotel"])  

  for (let iday = 0; iday < getNumDays(agenda); ++iday) {
    const date = moment(sd).add(iday, "days")
    const cols = []
    const items = _.compact(getDay(agenda, iday + 1))
    if (!items) break
    
    // 1st column, dates and shit
    cols[0] = [`Day ${iday + 1}`, date.format("YYYY.MM.DD")]

    // 2nd column, Cities
    cols[1] = getCitiesColumn(getCity)(items)
      
    // 3nd column, activities
    cols[2] = getActivityNamesColumn(getActivity)(items)
    cols[3] = getActivityTitlesColumn(getActivity)(items)
    // 4nd column, transportation
    
    transportations = transportColumnTreatment(getTransportColumn(getCity)(items), currentCity)
    cols[4] = [...transportations.transportations, transportations.transportations.length > 0 ? "& Local Public Transportation" : "Local Public Transportation"]
    
    // 5nd column, hotel, last day hotel is optional
    cols[5] = iday < getNumDays(agenda) - 1  
    ? getHotelColumn(items, expenses) 
    : includeLastHotel 
      ? getHotelColumn(items, expenses) 
      : ""

    // join all in rows
    const numRows = _.max(cols.map(c => c.length))
    const dayRows = _.range(numRows).map(i => cols.map(c => (c.length > i ? c[i] : "")))

    rows = [...rows, ...dayRows, emptyRow]
    if (iday == 0) currentCity = cols[1][cols[1].length - 1]; else currentCity = transportations.city
  }
  
  return { data: rows }
}

/**
 * 
 */

type PDVProps = {
  onClose: () => void, 
  trip: Object, 
  route: Object, 
  bill: Object, 
  getActivity: string => Object,
}

const stateToProps = (state, ownProps) => ({
  getCity: id => state.cities[id], 
  getActivity: id => state.activities[id], 
  bill: getBillForProps(state, ownProps), 
})

/**
 * 
 */
type PlanDeViajeProps = {
  onClose: () => void, 
  trip: Object, 
  route: Object, 
  bill: Object, 
  getCity: string => string, 
  getActivity: string => Object,
}

class PlanDeViaje extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { includeLastHotel: false }
  }
  props: PlanDeViajeProps
  render() {
    return (
      <Modal show onHide={() => this.props.onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>{getText("Descargar ruta para visado (plan de viaje)")} (<Glyphicon glyph="star" />)</Modal.Title> 
        </Modal.Header>
        <Modal.Body>
          <p>{getText('Este es el plan de viaje para las oficinas de visado.')}</p>
          <p>{getText('Recuerda revisar el documento antes de enviarlo.')}</p>
          <Button
            bsStyle="primary"
            glyph="download"
            onClick={() => {
              const { data, cityIndexes } = generateCSV(this.props.trip, this.props.route, this.props.bill, this.props.getCity, this.props.getActivity, this.state.includeLastHotel)
              
              FileSaver.saveAs(
                createExcelBlob(
                  data, 
                  planDeViajeStyle(data)
                ), 
                `Plan de viaje ${this.props.trip.name}.xlsx`
              )
            }}
          >
            {getText("Descargar XLSX")}
          </Button>
          <Checkbox
            checked={this.state.includeLastHotel}
            onChange={evt => this.setState({includeLastHotel : evt.target.checked})}
          >
            <span style={{fontWeight: "bold"}}>{getText("Incluir hotel último dia")}</span>
          </Checkbox>
        </Modal.Body>
        <Modal.Footer>
          <Button bsStyle="success" onClick={() => this.props.onClose()}>{getText("Cerrar")}</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}
export default connect(stateToProps, () => ({}))(PlanDeViaje)

